/**
 * The matcher — picking a line that actually answers what was said.
 *
 * Pure: no clock, no network, and no `Math.random`. Randomness is a parameter, which is the
 * only reason a function whose whole job is "pick an unpredictable line" can be unit-tested at
 * all. The caller supplies a number in [0, 1); the tests supply a fixed one.
 *
 * Scoring is term-frequency-free TF-IDF: a term that appears in two entries out of eighty says
 * far more about the topic than one that appears in thirty, so rare shared terms dominate. Tags
 * count double, because they are the editorial judgement about what a line is *about*, while
 * the body text merely happens to contain words.
 */

import { ENTRIES } from '../corpus/entries.js';
import { tokenize } from './tokenize.js';
import type { CorpusEntry, Lang, Match, Reply } from './types.js';

/** A tag match is worth this much more than a match in the body of the quote. */
export const TAG_WEIGHT = 2;
/** Below this total score the input is treated as unmatched, and a line is picked at random. */
export const MIN_SCORE = 0.9;
/**
 * Candidates scoring at least this fraction of the best are treated as equally good and one is
 * chosen at random among them. Kept tight on purpose: a wide band makes the app answer a
 * question about coffee with a proverb about friendship, because both matched one term.
 */
export const TIE_BAND = 0.92;

interface IndexedEntry {
  entry: CorpusEntry;
  tagTerms: Set<string>;
  textTerms: Set<string>;
}

interface LanguageIndex {
  entries: IndexedEntry[];
  /** Inverse document frequency per term. */
  idf: Map<string, number>;
}

/** Builds the searchable index for one language. Called once per corpus, memoized below. */
export function buildIndex(entries: readonly CorpusEntry[], lang: Lang): LanguageIndex {
  const forLang = entries.filter((e) => e.lang === lang);

  const indexed: IndexedEntry[] = forLang.map((entry) => ({
    entry,
    tagTerms: new Set(tokenize(entry.tags.join(' '), lang)),
    textTerms: new Set(tokenize(entry.text, lang)),
  }));

  const documentFrequency = new Map<string, number>();
  for (const item of indexed) {
    for (const term of new Set([...item.tagTerms, ...item.textTerms])) {
      documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
    }
  }

  const total = indexed.length || 1;
  const idf = new Map<string, number>();
  for (const [term, df] of documentFrequency) {
    idf.set(term, Math.log(1 + total / (1 + df)));
  }

  return { entries: indexed, idf };
}

const memo = new Map<string, LanguageIndex>();

function indexFor(entries: readonly CorpusEntry[], lang: Lang): LanguageIndex {
  // The default corpus is a module constant, so one cache entry per language covers every call.
  // A caller passing its own array skips the cache rather than risk a stale index.
  if (entries !== ENTRIES) return buildIndex(entries, lang);
  const cached = memo.get(lang);
  if (cached) return cached;
  const built = buildIndex(entries, lang);
  memo.set(lang, built);
  return built;
}

export interface MatchOptions {
  lang: Lang;
  /** Defaults to the bundled corpus. */
  entries?: readonly CorpusEntry[];
  /** Entry ids to skip, so the same line does not come back twice in a row. */
  exclude?: readonly string[];
}

/**
 * Scores every entry in the language against the input, best first.
 *
 * Entries that share nothing with the input are omitted entirely rather than returned with a
 * zero, so the caller can tell "nothing matched" from "everything matched weakly".
 */
export function findMatches(input: string, options: MatchOptions): Match[] {
  const { lang, entries = ENTRIES, exclude = [] } = options;
  const index = indexFor(entries, lang);
  const terms = tokenize(input, lang);
  if (terms.length === 0) return [];

  const skip = new Set(exclude);
  const matches: Match[] = [];

  for (const item of index.entries) {
    if (skip.has(item.entry.id)) continue;
    let score = 0;
    const matched: string[] = [];

    for (const term of terms) {
      const weight = index.idf.get(term);
      if (weight === undefined) continue;
      if (item.tagTerms.has(term)) {
        score += weight * TAG_WEIGHT;
        matched.push(term);
      } else if (item.textTerms.has(term)) {
        score += weight;
        matched.push(term);
      }
    }

    if (score > 0) matches.push({ entry: item.entry, score, matchedTerms: matched });
  }

  // Ties are broken by id so the ordering is stable across runs and across machines.
  return matches.sort((a, b) => b.score - a.score || a.entry.id.localeCompare(b.entry.id));
}

/**
 * Chooses one entry at random from a list, using a caller-supplied number.
 *
 * `randomValue` must be in [0, 1). Values outside that range are clamped rather than rejected,
 * because a reply is never worth throwing an exception over.
 */
function choose<T>(items: readonly T[], randomValue: number): T | undefined {
  if (items.length === 0) return undefined;
  const clamped = Math.min(0.999999, Math.max(0, randomValue));
  return items[Math.floor(clamped * items.length)];
}

export interface PickOptions extends MatchOptions {
  /** A number in [0, 1) supplied by the caller. Keeps this module pure. */
  randomValue: number;
}

/**
 * Picks the reply for a phrase.
 *
 * When several entries score close to the best, one of them is chosen at random — otherwise
 * the same input always returns the same line, which kills the joke on the second try. When
 * nothing matches at all, a random line in the right language is returned and flagged as such,
 * because pretending a random quote was a considered answer would be a small lie the UI would
 * then be telling on every miss.
 */
export function pickReply(input: string, options: PickOptions): Reply | undefined {
  const { lang, entries = ENTRIES, exclude = [], randomValue } = options;
  const matches = findMatches(input, { lang, entries, exclude });
  const best = matches[0];

  if (best && best.score >= MIN_SCORE) {
    const band = matches.filter((m) => m.score >= best.score * TIE_BAND);
    const chosen = choose(band, randomValue) ?? best;
    return {
      entryId: chosen.entry.id,
      text: chosen.entry.text,
      lang,
      attribution: chosen.entry.attribution,
      source: 'corpus',
      matchedTerms: chosen.matchedTerms,
      random: false,
    };
  }

  const skip = new Set(exclude);
  const pool = entries.filter((e) => e.lang === lang && !skip.has(e.id));
  const fallback = choose(pool, randomValue);
  if (!fallback) return undefined;

  return {
    entryId: fallback.id,
    text: fallback.text,
    lang,
    attribution: fallback.attribution,
    source: 'corpus',
    matchedTerms: [],
    random: true,
  };
}

/** How many lines the bundled corpus holds, per language. Shown in the About panel. */
export function corpusSize(entries: readonly CorpusEntry[] = ENTRIES): Record<Lang, number> {
  return {
    ru: entries.filter((e) => e.lang === 'ru').length,
    en: entries.filter((e) => e.lang === 'en').length,
  };
}
